import React from 'react';

import { $createLinkNode } from '@lexical/link';
import { $isAtNodeEnd } from '@lexical/selection';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  createCommand,
} from 'lexical';

import Text from 'components/SvgComponents/icons/Text';
import AlignCenter from 'components/SvgComponents/icons/text-editor/AlignCenter';
import AlignJustify from 'components/SvgComponents/icons/text-editor/AlignJustify';
import AlignLeft from 'components/SvgComponents/icons/text-editor/AlignLeft';
import AlignRight from 'components/SvgComponents/icons/text-editor/AlignRight';
import HeadingH1 from 'components/SvgComponents/icons/text-editor/HeadingH1';
import HeadingH2 from 'components/SvgComponents/icons/text-editor/HeadingH2';
import HeadingH3 from 'components/SvgComponents/icons/text-editor/HeadingH3';
import { parseToRgba } from 'utils/color.utils';

export const SET_COLOR_COMMAND = createCommand('SET_COLOR_COMMAND');
export const SET_HEADING_TAG_COMMAND = createCommand('SET_HEADING_TAG_COMMAND');

export const BLOCK_TYPE = {
  HEADING: 'heading',
  PARAGRAPH: 'paragraph',
  LIST: 'list',
};

export const HEADING_TAG = {
  PARAGRAPH: 'paragraph',
  H1: 'h1',
  H2: 'h2',
  H3: 'h3',
};

export const LIST_TAG = {
  NUMBERED_LIST: 'ol',
  BULLETED_LIST: 'ul',
};

export const TEXT_FORMAT = {
  BOLD: 'bold',
  ITALIC: 'italic',
  UNDERLINE: 'underline',
  LINK: 'link',
};

export const TEXT_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
  JUSTIFY: 'justify',
};

export const COLOR_CSS_PROPERTY = {
  COLOR: 'color',
  BACKGROUND_COLOR: 'background-color',
};

export const paragraphOption = {
  labelId: 'heading_normal',
  value: HEADING_TAG.PARAGRAPH,
  icon: <Text size={18} />,
};

export const h1Option = {
  labelId: 'heading_title_1',
  value: HEADING_TAG.H1,
  icon: <HeadingH1 />,
};

export const h2Option = {
  labelId: 'heading_title_2',
  value: HEADING_TAG.H2,
  icon: <HeadingH2 />,
};

export const h3Option = {
  labelId: 'heading_title_3',
  value: HEADING_TAG.H3,
  icon: <HeadingH3 />,
};

export const bulletedListOption = {
  labelId: 'bulleted_list',
  value: LIST_TAG.BULLETED_LIST,
  icon: <FormatListBulletedIcon fontSize="small" />,
};

export const numberedListOption = {
  labelId: 'numbered_list',
  value: LIST_TAG.NUMBERED_LIST,
  icon: <FormatListNumberedIcon fontSize="small" />,
};

export const alignLeftOption = {
  labelId: 'align_left',
  value: TEXT_ALIGN.LEFT,
  icon: <AlignLeft />,
};

export const alignCenterOption = {
  labelId: 'align_center',
  value: TEXT_ALIGN.CENTER,
  icon: <AlignCenter />,
};

export const alignRightOption = {
  labelId: 'align_right',
  value: TEXT_ALIGN.RIGHT,
  icon: <AlignRight />,
};

export const alignJustifyOption = {
  labelId: 'align_justify',
  value: TEXT_ALIGN.JUSTIFY,
  icon: <AlignJustify />,
};

const URL_MATCHER = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const EMAIL_MATCHER = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

export const MATCHERS = [
  text => {
    const match = URL_MATCHER.exec(text);
    if (match === null) {
      return null;
    }
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    };
  },
  text => {
    const match = EMAIL_MATCHER.exec(text);
    if (match === null) {
      return null;
    }
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: `mailto:${fullMatch}`,
    };
  },
];

export const getSelectedNode = selection => {
  const { anchor } = selection;
  const { focus } = selection;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  }
  return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
};

export const isHtmlEmpty = html => {
  if (!html || typeof html !== 'string') return true;

  const div = document.createElement('div');
  div.innerHTML = html;
  if (html === '<p><br></p>') return true;
  return !div.hasChildNodes();
};

export const getSanitizedHtmlContent = html => {
  if (!html || typeof html !== 'string') return '';

  const div = document.createElement('div');

  // Replace spaces in empty paragraphs with &nbsp; to preserve spacing
  div.innerHTML = html.replace(
    /<p>(\s+)<\/p>/g,
    (_, spaces) => `<p>${'&nbsp;'.repeat(spaces.length)}</p>`,
  );

  // Remove all empty <p> elements
  const paragraphs = div.querySelectorAll('p');
  paragraphs.forEach(p => {
    if (paragraphs.length === 1) {
      if (p.innerHTML.trim() === '<br>' || p.innerHTML.trim() === '') p.remove();
    } else if (p.innerHTML.trim() === '') {
      p.remove();
    }
  });

  return div.innerHTML;
};

export const patchStyleConversion = originalDOMConverter => node => {
  const original = originalDOMConverter?.(node);
  if (!original) return null;

  const originalOutput = original.conversion(node);
  if (!originalOutput) return originalOutput;

  const { backgroundColor, color, fontFamily, fontWeight, fontSize, textDecoration } = node.style;
  return {
    ...originalOutput,
    forChild: (lexicalNode, parent) => {
      const originalForChild = originalOutput?.forChild ?? (x => x);
      const result = originalForChild(lexicalNode, parent);
      if ($isTextNode(result)) {
        const style = [
          backgroundColor ? `background-color: ${parseToRgba(backgroundColor)}` : null,
          color ? `color: ${parseToRgba(color)}` : null,
          fontFamily ? `font-family: ${fontFamily}` : null,
          fontWeight ? `font-weight: ${fontWeight}` : null,
          fontSize ? `font-size: ${fontSize}` : null,
          textDecoration ? `text-decoration: ${textDecoration}` : null,
        ]
          .filter(value => value != null)
          .join('; ');
        if (style.length) {
          return result.setStyle(style);
        }
      }
      return result;
    },
  };
};

export const editorTheme = {
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
  },
};

const findMatchInText = text => {
  let match = null;
  MATCHERS.some(matcher => {
    match = matcher(text);
    return !!match;
  });
  return match;
};

export const linkifyLastWord = () => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

  const anchorNode = selection.anchor.getNode();
  if (!$isTextNode(anchorNode)) return false;

  const textContent = anchorNode.getTextContent();
  const cursorOffset = selection.anchor.offset;

  // find the last "word" before cursor (separated by space)
  const textBeforeCursor = textContent.slice(0, cursorOffset);
  const lastSpaceIndex = textBeforeCursor.lastIndexOf(' ');
  const lastWord = textBeforeCursor.slice(lastSpaceIndex + 1);

  if (!lastWord) return false;

  const match = findMatchInText(lastWord);
  if (!match || match.text !== lastWord) return false;

  // split the TextNode into 3 parts: before link | link | after cursor
  const startOffset = lastSpaceIndex + 1;

  // split the node to isolate link text
  let targetNode = anchorNode;
  let linkStartOffset = startOffset;

  // text before link
  if (linkStartOffset > 0) {
    [, targetNode] = anchorNode.splitText(linkStartOffset);
    linkStartOffset = 0;
  }

  // text after link (if there is content after cursor)
  const linkLength = match.text.length;
  const remainingText = targetNode.getTextContent().slice(linkLength);
  if (remainingText.length > 0) {
    [targetNode] = targetNode.splitText(linkLength);
  }

  // create LinkNode and replace text
  const linkNode = $createLinkNode(match.url);
  const linkTextNode = $createTextNode(match.text);
  linkNode.append(linkTextNode);
  targetNode.replace(linkNode);

  // reposition cursor after link
  const nextSibling = linkNode.getNextSibling();
  if (nextSibling && $isTextNode(nextSibling)) {
    selection.anchor.set(nextSibling.getKey(), 0, 'text');
    selection.focus.set(nextSibling.getKey(), 0, 'text');
  } else {
    // create empty text node after link to continue typing
    const spaceNode = $createTextNode('');
    linkNode.insertAfter(spaceNode);
    selection.anchor.set(spaceNode.getKey(), 0, 'text');
    selection.focus.set(spaceNode.getKey(), 0, 'text');
  }

  return false;
};

export const splitTextIntoSegments = text => {
  const segments = [];
  let remaining = text;

  const reduceToEarliestMatch = (earliest, matcher) => {
    const match = matcher(remaining);
    if (match && (!earliest || match.index < earliest.index)) return match;
    return earliest;
  };

  while (remaining.length > 0) {
    const earliestMatch = MATCHERS.reduce(reduceToEarliestMatch, null);

    if (!earliestMatch) {
      segments.push({ type: 'text', text: remaining });
      break;
    }

    if (earliestMatch.index > 0) {
      segments.push({ type: 'text', text: remaining.slice(0, earliestMatch.index) });
    }

    segments.push({ type: 'link', text: earliestMatch.text, url: earliestMatch.url });
    remaining = remaining.slice(earliestMatch.index + earliestMatch.length);
  }

  return segments;
};
